import React from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import gtmTracker from '@utils/gtmTracker';

import { useModals } from '@shared/contexts';
import useIsOverflowing from '@shared/hooks/use-is-overflowing';

import { useMutationBuyShopItem } from '@hooks/api/shop';
import { useQueryGetMe } from '@hooks/api/user/use-query-me';
import { useCompareWindowWidth } from '@hooks/use-compare-window-width.ts';
import useEnglishTranslation from '@hooks/use-english-translation';
import useGetPointsIcon from '@hooks/use-get-points-icon';

import { Category, ITEM_TYPE, SHOP_CHIP_STATUS, type ShopItem as ShopItemType } from '@types';

import { Button } from '@components/button';
import { ModalName } from '@components/modals';

import { config } from '@/config.ts';

import * as Styled from './shop-item.styled';
import { TagChip } from './tag-chip';

const isSk2 = config.BRAND === 'sk2';

const buyBtnText = isSk2 ? 'buy' : 'take';

type OwnProps = {
	shopItem: ShopItemType;
	categories: Category[];
};

const ShopItem: React.FC<OwnProps> = ({ shopItem, categories }) => {
	const PointsIcon = useGetPointsIcon();
	const { t } = useTranslation('general');
	const tEn = useEnglishTranslation('general');
	const { buyItem, isLoading } = useMutationBuyShopItem();
	const { openModal } = useModals();
	const { data: user } = useQueryGetMe();
	const isMobile = useCompareWindowWidth(1200);
	const textRef = React.useRef<HTMLDivElement>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const { isOverflowing } = useIsOverflowing(containerRef, textRef, 'vertical');

	const isAuthenticated = !!user?.pm_id;

	const handleTicketsBuy = () => {
		if (!isAuthenticated) {
			openModal(ModalName.LOGIN);
			return;
		}
		openModal(ModalName.PROMOTION_TICKETS, { ticket: shopItem });
	};

	const navigate = useNavigate();

	const handleTrackEvent = (label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Shop_card',
			event_action: 'click',
			event_label: label
		});
	};

	const handleDetailedPromoClick = (id: string) => {
		handleTrackEvent(tEn('details'));
		navigate('/promotion', { state: { promotionId: id } });
	};

	const handleBuyShopItem = () => {
		handleTrackEvent(tEn(isSk2 ? 'buy' : 'take'));
		if (!isAuthenticated) {
			openModal(ModalName.LOGIN);
			return;
		}

		try {
			buyItem(shopItem.item_type, shopItem.id);
		} catch (err) {}
	};

	const handleDetailsModal = (shopItem: ShopItemType) => {
		handleTrackEvent(tEn('details'));
		openModal(ModalName.SHOP_ITEM, { shopItem: shopItem });
	};

	const shouldShowDetailsButton = !!shopItem.description && (isMobile || isOverflowing.vertical);

	const isStockInfinite = !!shopItem.infinite_stock;
	const canBuy = shopItem.items_in_stock > 0 || isStockInfinite;

	return (
		<Styled.ShopItemWrapper>
			<Styled.ChipWrapper>
				{shopItem.new_item &&
					categories
						.filter(category => category.new_item === true)
						.map((category, index) => <TagChip key={index} name={category.name} state={SHOP_CHIP_STATUS.NEW} />)}

				{shopItem.hot_item &&
					categories
						.filter(category => category.hot_item === true)
						.map((category, index) => <TagChip key={index} name={category.name} state={SHOP_CHIP_STATUS.HOT} />)}
			</Styled.ChipWrapper>

			<Styled.ShopItemImage $disabled={!canBuy} src={shopItem.image ?? ''} />

			<Styled.ShopItemTitleWrapper>
				<Styled.ShopItemReward>
					{shopItem.price}
					{isSk2 ? ' ' + t('points_p') : ''}
					{PointsIcon}
				</Styled.ShopItemReward>

				{!isStockInfinite && canBuy && (
					<Styled.ShopItemCount>
						{shopItem.items_in_stock}/{shopItem.initial_amount}
					</Styled.ShopItemCount>
				)}
			</Styled.ShopItemTitleWrapper>

			<Styled.Divider />
			<Styled.ShopItemInfo>
				<Styled.ShopItemName>{shopItem.name}</Styled.ShopItemName>
				<Styled.ShopItemDescription ref={containerRef}>
					<Styled.ShopItemTextDescInner ref={textRef} dangerouslySetInnerHTML={{ __html: shopItem.description }} />
				</Styled.ShopItemDescription>
			</Styled.ShopItemInfo>
			<Styled.ShopItemButtons>
				{shopItem.item_type === ITEM_TYPE.LOTTERY ? (
					<Button
						mobileSize='extra_small'
						onClick={() => handleDetailedPromoClick(shopItem.lottery_id)}
						color='neutral'
					>
						{t('details')}
					</Button>
				) : (
					shouldShowDetailsButton && (
						<Button mobileSize='extra_small' onClick={() => handleDetailsModal(shopItem)} color='neutral'>
							{t('details')}
						</Button>
					)
				)}
				<Button
					minWidth={70}
					color='primary'
					onClick={shopItem.item_type === ITEM_TYPE.LOTTERY ? handleTicketsBuy : handleBuyShopItem}
					disabled={isLoading || !canBuy}
					mobileSize='extra_small'
				>
					{t(canBuy ? buyBtnText : 'out_of_stock')}
				</Button>
			</Styled.ShopItemButtons>
		</Styled.ShopItemWrapper>
	);
};

export default ShopItem;
